import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FaEye, FaEdit } from "react-icons/fa";
import { Button, Table, Modal, Input, Space, Spin, Row, Col, DatePicker } from "antd";
import { IoExit } from "react-icons/io5";
import { useNavigate } from "react-router-dom";
import { toLocalDateTime } from '../../utils/DateUtil';
import {
  updateVisitorRequest,
  fetchVisitorRequest,
  deleteVisitorRequest,
} from "../../redux/actions/visitorActions";
import { fetchSiteRequest } from "../../redux/actions/siteActions";
import EditVisitorForm from "../Forms/Visitor/EditVisitor";
import ViewVisitorDetails from "../Forms/Visitor/ViewVisitorDetails";
import ConfirmationModal from "../ConfirmationModel/ConfirmationModel";
import dayjs from "dayjs"; 
import "./ViewEmployee.css";

const ViewVisitors = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("user"));

  // Redux State
  const { data = [], loading } = useSelector((state) => state.visitor || {});
  const { RangePicker } = DatePicker;

  // Local State
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredVisitors, setFilteredVisitors] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(15); // Items per page
  const [selectedVisitor, setSelectedVisitor] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isConfirmModalVisible, setIsConfirmModalVisible] = useState(false);
  const [visitorToDelete, setVisitorToDelete] = useState(null);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [selectedSite] = useState(); 

  // Initialize the date range to "yesterday to today"
  useEffect(() => {
    const today = dayjs();
    const yesterday = dayjs().subtract(1, 'day');
    
    setFromDate(yesterday);
    setToDate(today);
  }, []);

  useEffect(() => {
    dispatch(fetchSiteRequest());
  }, [dispatch]);

  useEffect(() => {
    const fromDateStr = fromDate ? fromDate.toISOString() : null;
    const toDateStr = toDate ? toDate.toISOString() : null;

    dispatch(fetchVisitorRequest({
        siteId: user?.role === "SUPERADMIN" ? selectedSite : user?.empsiteid,
      fromDate: fromDateStr,
      toDate: toDateStr
    }));
  }, [dispatch, selectedSite, fromDate, toDate, user?.role, user?.empsiteid]);

  // Update filtered visitors when data or searchQuery changes
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!searchQuery) {
        // Filter only "Time-In" visitors (those with a 'timein' but no 'timeout')
        const filtered = data.filter(
          (visitor) =>
            visitor.timein && !visitor.timeout && (
              visitor.recono.toString().includes(searchQuery) ||
              visitor.visitorname.toLowerCase().includes(searchQuery.toLowerCase())
            )
        );
        setFilteredVisitors(filtered);
      } else {
        const filtered = data.filter(
          (visitor) =>
            visitor.timein && !visitor.timeout && (
              visitor.recono.toString().includes(searchQuery) ||
              visitor.visitorname.toLowerCase().includes(searchQuery.toLowerCase())
            )
        );
        setFilteredVisitors(filtered);
      }
    }, 300); // Debounce search for better performance

    return () => clearTimeout(timer);
  }, [searchQuery, data]);

  // Pagination Logic: Calculate paginated visitors
  const paginatedVisitors = filteredVisitors.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  // Add Visitor Button Handler
  const handleAddVisitor = () => {
    navigate("/visitorform");
  };

  // Open Modal to Edit Visitor
  const handleEditClick = (visitor) => {
    setSelectedVisitor(visitor);
    setIsEditMode(true);
    setIsModalOpen(true);
  };

  // Open Modal to View Visitor Details
  const handleViewDetails = (visitor) => {
    setSelectedVisitor(visitor);
    setIsEditMode(false);
    setIsModalOpen(true);
  };

  // Handle Save/Update Visitor
  const handleEditVisitor = (updatedVisitor) => {
    dispatch(updateVisitorRequest(updatedVisitor));
    setIsModalOpen(false);
  };

  // Open Delete Confirmation Modal
  const openDeleteModal = (visitor) => {
    setVisitorToDelete(visitor);
    setIsConfirmModalVisible(true);
  };

  // Confirm Deletion
  const handleConfirmDelete = () => {
    if (visitorToDelete) {
      dispatch(deleteVisitorRequest(visitorToDelete));
      setIsConfirmModalVisible(false);
      setVisitorToDelete(null);
    }
  };

  // Cancel Deletion
  const handleCancelDelete = () => {
    setIsConfirmModalVisible(false);
    setVisitorToDelete(null);
  };

  // Close Modal
  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedVisitor(null);
    setIsEditMode(false);
  };

  // Table Columns
  const columns = [
    {
      title: "Sr. No.",
      render: (_, __, index) => (currentPage - 1) * pageSize + index + 1,
    },
    {
      title: "Visitor Name",
      dataIndex: "visitorname",
      sorter: (a, b) => a.visitorname.localeCompare(b.visitorname),
    },
    {
      title: "Visitor Type",
      dataIndex: "visitortype",
    },
    {
      title: "Company Name",
      dataIndex: "companyname",
    },
    {
      title: "Reco No",
      dataIndex: "recono",
    },
    {
      title: "Phone",
      dataIndex: "phone",
    },
    {
      title: "Time In",
      dataIndex: "timein",
      render: (timein) => (timein ? toLocalDateTime(timein) : "N/A"),
    },
    {
      title: "Actions",
      render: (_, record) => (
        <Space size="middle">
          <Button icon={<FaEye />} onClick={() => handleViewDetails(record)} />
          <Button icon={<FaEdit />} onClick={() => handleEditClick(record)} />
          <Button
            icon={<IoExit />}
            onClick={() => openDeleteModal(record)}
            danger
          />
        </Space>
      ),
    },
  ];

  return (
    <div className="visitors-table-container">
      <div className="actions-container">
        <div className="view-employee-text">View Visitors</div>
        <Button type="primary" className="addbutton" onClick={handleAddVisitor}>
          + Add Visitor
        </Button>
      </div>

      {/* Filters Container */}
      <div className="filters-container">
        <Row gutter={[16, 16]} justify="start" align="middle">
          <Col xs={24} sm={8} md={6} lg={5}>
            <RangePicker
              style={{ width: '100%' }}
              onChange={(dates) => {
                setFromDate(dates ? dates[0] : null);
                setToDate(dates ? dates[1] : null);
              }}
              value={[fromDate, toDate]}
              placeholder={['From Date', 'To Date']}
            />
          </Col>


        </Row>
      </div>

      {/* Search Bar */}
      <div className="search-container">
        <Input
          type="text"
          placeholder="Search by Visitor ID or Name..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="search-input"
        />
      </div>

      {/* Table Container with Scroll */}
      <div className="table-container">
        <Spin spinning={loading} tip="Loading visitors...">
          <Table
            columns={columns}
            dataSource={paginatedVisitors}
            rowKey="Sr no"
            pagination={{
              current: currentPage,
              pageSize,
              total: filteredVisitors.length,
              onChange: (page) => setCurrentPage(page),
            }}
          />
        </Spin>
      </div>

      {/* Modals */}
      {isModalOpen && selectedVisitor && (
        <Modal
          title={isEditMode ? "Edit Visitor Details" : "Visitor Details"}
          open={isModalOpen}
          onCancel={closeModal}
          footer={null}
        >
          {isEditMode ? (
            <EditVisitorForm
              visitor={selectedVisitor}
              onSave={handleEditVisitor}
            />
          ) : (
            <ViewVisitorDetails visitor={selectedVisitor} />
          )}
        </Modal>
      )}

      {/* Confirmation Modal */}
      <ConfirmationModal
        visible={isConfirmModalVisible}
        onCancel={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        deletetext={"Time-Out"}
        message={"Are you sure you want to Time Out the Visitor?"}
      />
    </div>
  );
};

export default ViewVisitors;
